import { useMemo } from "react";
import { Download, Loader2 } from "lucide-react";
import { useDashboardChart, useLabs } from "@/hooks/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { AutoChart } from "@/components/shared/auto-chart";
import { downloadCsv, toCsv } from "@/lib/utils/csv";
import { groupOverTime, pivotOverTime, type OverTimeRow } from "@/lib/utils/pivotOverTime";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";
import { MultiSelect } from "./multi-select";

const DASHBOARD_ID = "lab-usage";
const NO_LAB = "Uten lab";
// AutoChart renders nothing above this many rows, which "Dag" over a year exceeds.
const AUTO_CHART_MAX_ROWS = 200;

const METRICS = [
  { value: "all", label: "Alle" },
  { value: "completed", label: "Gjennomført" },
  { value: "planned", label: "Rekvirert" },
] as const;

const GRANULARITIES = [
  { value: "month", label: "Måned" },
  { value: "day", label: "Dag" },
] as const;

// Semicolon and a BOM so Norwegian Excel opens the file in columns with æøå intact.
const CSV_DELIMITER = ";";

type LabTotalsRow = {
  lab_id: string | null;
  lab_name: string | null;
  city: string | null;
  completed: number;
  planned: number;
};

// "By" is not column-filtered: the page's By multi-select above already
// drives the query for every card on this dashboard.
const LAB_TOTALS_COLUMNS: DashboardTableColumn<LabTotalsRow>[] = [
  { id: "lab_name", label: "Lab", value: (r) => r.lab_name ?? NO_LAB },
  { id: "city", label: "By", value: (r) => r.city ?? "—" },
  { id: "completed", label: "Gjennomført", value: (r) => r.completed, numeric: true, align: "right" },
  { id: "planned", label: "Rekvirert", value: (r) => r.planned, numeric: true, align: "right" },
  {
    id: "total",
    label: "Totalt",
    value: (r) => r.completed + r.planned,
    numeric: true,
    align: "right",
  },
];

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split("T")[0]!;
}

/** Comma-joined in the URL — `useDashboardUrlState` stores strings only. */
function parseList(value: string): string[] {
  return value ? value.split(",").filter(Boolean) : [];
}

const cityLabel = (r: OverTimeRow) => r.city ?? NO_LAB;

export function LabUsageDashboard() {
  const [{ from, to, cities, metric, granularity }, update] =
    useDashboardUrlState({
      from: isoDaysAgo(365),
      to: isoDaysAgo(0),
      cities: "",
      metric: "all",
      granularity: "month",
    });

  const { data: labs } = useLabs();
  const cityOptions = useMemo(
    () =>
      [...new Set((labs ?? []).map((l) => l.city))].map((city) => ({
        value: city,
        label: city,
      })),
    [labs],
  );

  const selectedCities = useMemo(() => parseList(cities), [cities]);
  const params = useMemo(
    () => ({
      from,
      to,
      cities: selectedCities.length ? selectedCities : undefined,
    }),
    [from, to, selectedCities],
  );
  // Separate object so toggling granularity doesn't refetch the totals.
  const overTimeParams = useMemo(
    () => ({ ...params, granularity }),
    [params, granularity],
  );

  const overTime = useDashboardChart<typeof overTimeParams, OverTimeRow>(
    DASHBOARD_ID,
    "visits-over-time",
    overTimeParams,
  );
  const labTotals = useDashboardChart<typeof params, LabTotalsRow>(
    DASHBOARD_ID,
    "lab-totals",
    params,
  );
  const labTotalsTable = useDashboardTable(
    "lab-totals",
    LAB_TOTALS_COLUMNS,
    labTotals.data,
  );

  // Series label per lab_id. Lab names aren't unique, and AutoChart keys series
  // by label, so a name shared by several labs gets the id appended.
  const labLabel = useMemo(() => {
    const names = new Map<string | null, string>();
    for (const r of overTime.data ?? []) names.set(r.lab_id, r.lab_name ?? NO_LAB);
    const uses = new Map<string, number>();
    for (const n of names.values()) uses.set(n, (uses.get(n) ?? 0) + 1);
    return (r: OverTimeRow) => {
      const name = r.lab_name ?? NO_LAB;
      return uses.get(name)! > 1 && r.lab_id ? `${name} (${r.lab_id.slice(0, 8)})` : name;
    };
  }, [overTime.data]);

  const chartRows = useMemo(
    () => pivotOverTime(overTime.data ?? [], labLabel, metric),
    [overTime.data, metric, labLabel],
  );
  const cityChartRows = useMemo(
    () => pivotOverTime(overTime.data ?? [], cityLabel, metric),
    [overTime.data, metric],
  );

  // Grouped rows, not the pivot: every metric, independent of the metric toggle.
  const exportOverTime = (
    column: string,
    label: (r: OverTimeRow) => string,
    slug: string,
  ) => {
    const rows = groupOverTime(overTime.data ?? [], label);
    const csv = toCsv(
      [granularity === "day" ? "Dag" : "Måned", column, "Gjennomført", "Rekvirert", "Totalt"],
      rows.map((r) => [
        r.period,
        r.label,
        r.completed,
        r.planned,
        r.completed + r.planned,
      ]),
      CSV_DELIMITER,
    );
    downloadCsv(
      csv,
      `labbesok-${slug}per-${granularity === "day" ? "dag" : "maned"}-${from}-${to}.csv`,
      { bom: true },
    );
  };

  const labTotalsSum = (labTotals.data ?? []).reduce(
    (s, r) => ({ completed: s.completed + r.completed, planned: s.planned + r.planned }),
    { completed: 0, planned: 0 },
  );

  const exportLabTotals = () => {
    const rows = labTotalsTable.viewRows;
    const csv = toCsv(
      ["Lab", "By", "Gjennomført", "Rekvirert", "Totalt"],
      rows.map((r) => [
        r.lab_name ?? NO_LAB,
        r.city,
        r.completed,
        r.planned,
        r.completed + r.planned,
      ]),
      CSV_DELIMITER,
    );
    downloadCsv(csv, `labbesok-per-lab-${from}-${to}.csv`, { bom: true });
  };

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Fra</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => update({ from: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Til</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => update({ to: e.target.value })}
              className="w-44"
            />
          </label>
          <div className="space-y-1 flex-1 min-w-[15rem]">
            <span className="text-xs text-admin-text-subtle">By</span>
            <MultiSelect
              options={cityOptions}
              selected={selectedCities}
              onChange={(next) => update({ cities: next.join(",") })}
              placeholder="Alle byer"
              searchPlaceholder="Søk by…"
            />
          </div>
        </div>
      </FiltersCard>

      <OverTimeCard
        title="Besøk per lab over tid"
        rows={chartRows}
        query={overTime}
        granularity={granularity}
        metric={metric}
        update={update}
        onExport={() => exportOverTime("Lab", labLabel, "")}
      />

      <OverTimeCard
        title="Besøk per by over tid"
        rows={cityChartRows}
        query={overTime}
        granularity={granularity}
        metric={metric}
        update={update}
        onExport={() => exportOverTime("By", cityLabel, "per-by-")}
      />

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Per lab</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={exportLabTotals}
            disabled={!labTotals.data || labTotals.data.length === 0}
          >
            <Download className="h-3 w-3" />
            Eksporter CSV
          </Button>
        </CardHeader>
        <CardContent>
          {labTotals.isLoading ? (
            <ChartSkeleton />
          ) : labTotals.isError ? (
            <ErrorBlock message={labTotals.error?.message} />
          ) : !labTotals.data || labTotals.data.length === 0 ? (
            <EmptyBlock>Ingen labbesøk i perioden.</EmptyBlock>
          ) : (
            <DashboardTable
              table={labTotalsTable}
              columns={LAB_TOTALS_COLUMNS}
              renderRow={(row) => (
                <TableRow key={row.lab_id ?? "none"}>
                  <TableCell className="font-medium text-admin-text-primary">
                    {row.lab_name ?? NO_LAB}
                  </TableCell>
                  <TableCell className="text-admin-text-secondary">
                    {row.city ?? "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.completed}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.planned}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.completed + row.planned}
                  </TableCell>
                </TableRow>
              )}
              footer={
                <tfoot className="border-t border-admin-card-border font-medium text-admin-text-primary">
                  <TableRow>
                    <TableCell colSpan={2}>Totalt</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {labTotalsSum.completed}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {labTotalsSum.planned}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {labTotalsSum.completed + labTotalsSum.planned}
                    </TableCell>
                  </TableRow>
                </tfoot>
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function OverTimeCard({
  title,
  rows,
  query,
  granularity,
  metric,
  update,
  onExport,
}: {
  title: string;
  rows: Record<string, unknown>[];
  query: { data?: OverTimeRow[]; isLoading: boolean; isError: boolean; error: Error | null };
  granularity: string;
  metric: string;
  update: (patch: { granularity?: string; metric?: string }) => void;
  onExport: () => void;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle>{title}</CardTitle>
        <div className="flex flex-wrap gap-2">
          {GRANULARITIES.map((g) => (
            <Button
              key={g.value}
              variant={granularity === g.value ? "default" : "outline"}
              size="sm"
              onClick={() => update({ granularity: g.value })}
            >
              {g.label}
            </Button>
          ))}
          <span className="w-px bg-admin-card-border" />
          {METRICS.map((m) => (
            <Button
              key={m.value}
              variant={metric === m.value ? "default" : "outline"}
              size="sm"
              onClick={() => update({ metric: m.value })}
            >
              {m.label}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            disabled={!query.data || query.data.length === 0}
          >
            <Download className="h-3 w-3" />
            Eksporter CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <ChartSkeleton />
        ) : query.isError ? (
          <ErrorBlock message={query.error?.message} />
        ) : rows.length === 0 ? (
          <EmptyBlock>Ingen labbesøk i perioden.</EmptyBlock>
        ) : rows.length > AUTO_CHART_MAX_ROWS ? (
          <EmptyBlock>
            For mange dager å vise — velg en kortere periode eller Måned.
          </EmptyBlock>
        ) : (
          <AutoChart rows={rows} />
        )}
      </CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      Laster…
    </div>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-admin-text-subtle py-6 text-center">
      {children}
    </p>
  );
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}
